
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PurchasePapers.css";
import API_BASE_URL from "../config/config";

/*
|--------------------------------------------------------------------------
| Convert HTML Content To Plain Text
|--------------------------------------------------------------------------
*/

function stripHtml(html) {
    if (!html) return "";

    return String(html)
        // Remove script and style contents
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
        .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "")

        // Replace common block and line-break tags with spaces
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, " ")

        // Remove remaining HTML tags
        .replace(/<[^>]*>/g, "")

        // Decode common HTML entities
        .replace(/&nbsp;|&#160;|&#xA0;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&#(\d+);/g, (match, dec) => {
            const code = Number(dec);

            return code >= 0 && code <= 0x10FFFF
                ? String.fromCodePoint(code)
                : match;
        })
        .replace(/&#x([0-9a-f]+);/gi, (match, hex) => {
            const code = parseInt(hex, 16);

            return code >= 0 && code <= 0x10FFFF
                ? String.fromCodePoint(code)
                : match;
        })

        // Clean extra spaces
        .replace(/\s+/g, " ")
        .trim();
}

function PurchasePapers() {
    const navigate = useNavigate();

    const [papers, setPapers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    /*
    |--------------------------------------------------------------------------
    | Fetch Home Papers
    | Public API - No Login / No Token
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const fetchPapers = async () => {
            try {
                setLoading(true);
                setError("");

                const response = await fetch(
                    `${API_BASE_URL}/home-papers`,
                    {
                        headers: {
                            Accept: "application/json",
                        },
                    }
                );

                console.log(
                    "Home Papers API Status:",
                    response.status
                );

                const text = await response.text();

                console.log(
                    "Home Papers API Response:",
                    text
                );

                let data;

                try {
                    data = JSON.parse(text);
                } catch (jsonError) {
                    throw new Error(
                        `Server returned invalid JSON. HTTP Status: ${response.status}`
                    );
                }

                if (!response.ok) {
                    throw new Error(
                        data.message ||
                        `Failed to fetch papers (${response.status})`
                    );
                }

                if (data.success) {
                    console.log(
                        "Home Papers Data:",
                        data.papers
                    );

                    setPapers(
                        Array.isArray(data.papers)
                            ? data.papers
                            : []
                    );
                } else {
                    setPapers([]);

                    setError(
                        data.message ||
                        "No papers available."
                    );
                }
            } catch (error) {
                console.error(
                    "Home Papers API Error:",
                    error
                );

                setError(
                    error.message ||
                    "Unable to load papers."
                );

                setPapers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPapers();
    }, []);

    /*
    |--------------------------------------------------------------------------
    | Check Login & Buy Paper
    |--------------------------------------------------------------------------
    */

    const handleBuy = async (item) => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/user/me`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                    },
                }
            );

            console.log(
                "Login Check Status:",
                response.status
            );

            // User not logged in
            if (response.status === 401) {
                navigate("/login");
                return;
            }

            // Other error
            if (!response.ok) {
                console.error(
                    "Unable to check login status."
                );
                return;
            }

            // User logged in
            navigate("/subscription");
        } catch (error) {
            console.error(
                "Buy Paper Error:",
                error
            );
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {
        return (
            <section className="papers-section">
                <div className="papers-container">
                    <div className="papers-heading">
                        <span className="section-badge">
                            PREMIUM TEST SERIES
                        </span>

                        <h2 className="section-title">
                            Purchase New Papers
                        </h2>

                        <p className="section-subtitle">
                            Get latest test series & practice papers
                        </p>
                    </div>

                    <div className="papers-loading">
                        <div className="loading-spinner"></div>

                        <span>
                            Loading papers...
                        </span>
                    </div>
                </div>
            </section>
        );
    }

    /*
    |--------------------------------------------------------------------------
    | No Papers
    |--------------------------------------------------------------------------
    */

    if (papers.length === 0) {
        if (error) {
            console.error("Papers Error:", error);
        }

        return null;
    }

    /*
    |--------------------------------------------------------------------------
    | Main UI
    |--------------------------------------------------------------------------
    */

    return (
        <section className="papers-section">
            <div className="papers-container">

                {/* SECTION HEADING */}

                <div className="papers-heading">
                    <span className="section-badge">
                        PREMIUM TEST SERIES
                    </span>

                    <h2 className="section-title">
                        Purchase New Papers
                    </h2>

                    <p className="section-subtitle">
                        Get latest test series & practice papers
                    </p>
                </div>

                {/* PAPERS GRID */}

                <div className="papers-grid">
                    {papers.map((item) => (
                        <article
                            className="paper-card"
                            key={item.id}
                        >

                            {/* CARD TOP */}

                            <div className="paper-card-top">
                                <div className="premium-label">
                                    <span className="premium-dot"></span>
                                    PREMIUM
                                </div>

                                <div className="paper-icon">
                                    <span>✦</span>
                                </div>
                            </div>

                            {/* PAPER TITLE */}

                            <h3 className="paper-title">
                                {stripHtml(item.paper)}
                            </h3>

                            {/* DESCRIPTION */}

                            <div className="paper-description">
                                {item.detail1 && stripHtml(item.detail1) && (
                                    <div className="detail-item">
                                        <span className="detail-check">
                                            ✓
                                        </span>

                                        <span>
                                            {stripHtml(item.detail1)}
                                        </span>
                                    </div>
                                )}

                                {item.detail2 && stripHtml(item.detail2) && (
                                    <div className="detail-item">
                                        <span className="detail-check">
                                            ✓
                                        </span>

                                        <span>
                                            {stripHtml(item.detail2)}
                                        </span>
                                    </div>
                                )}

                                {item.detail3 && stripHtml(item.detail3) && (
                                    <div className="detail-item">
                                        <span className="detail-check">
                                            ✓
                                        </span>

                                        <span>
                                            {stripHtml(item.detail3)}
                                        </span>
                                    </div>
                                )}

                                {item.detail4 && stripHtml(item.detail4) && (
                                    <div className="detail-item">
                                        <span className="detail-check">
                                            ✓
                                        </span>

                                        <span>
                                            {stripHtml(item.detail4)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* DIVIDER */}

                            <div className="paper-divider"></div>

                            {/* PRICE + BUY */}

                            <div className="paper-bottom">
                                <div className="price-box">
                                    <span className="price-label">
                                        Course Price
                                    </span>

                                    <div className="price">
                                        <span className="currency">
                                            ₹
                                        </span>

                                        {item.price}
                                    </div>
                                </div>

                                {/* BUY BUTTON */}

                                <button
                                    type="button"
                                    className="buy-btn"
                                    onClick={() => handleBuy(item)}
                                >
                                    <span>
                                        Buy Now
                                    </span>

                                    <span className="buy-arrow">
                                        →
                                    </span>
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default PurchasePapers;
